import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getApiUrl, apiRequest } from '../config/api';
import SuccessToast from './SuccessToast';

const REGLA_CONTRASENA_NUEVA = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[^\s]{8,128}$/;
const AVISO_CONTRASENA_DEBIL =
  'La contraseña debe contener al menos una mayúscula, una minúscula, un número y un carácter especial';

const mensajeDeCambioDeContrasena = (cuerpo) => {
  if (!cuerpo || typeof cuerpo !== 'object') return 'No se pudo cambiar la contraseña';
  if (typeof cuerpo.message === 'string' && cuerpo.message.trim()) return cuerpo.message;
  if (typeof cuerpo.error === 'string' && cuerpo.error.trim()) return cuerpo.error;
  const deCampos = ['newPassword', 'currentPassword']
    .map(campo => cuerpo[campo])
    .filter(valor => typeof valor === 'string' && valor.trim());
  if (deCampos.length > 0) return deCampos.join(' ');
  return 'No se pudo cambiar la contraseña';
};

function AccountMenu({ onLogout }) {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);
  const raizRef = useRef(null);

  useEffect(() => {
    if (!menuAbierto) return undefined;
    const cerrar = (evento) => {
      if (raizRef.current && !raizRef.current.contains(evento.target)) {
        setMenuAbierto(false);
      }
    };
    document.addEventListener('mousedown', cerrar);
    return () => document.removeEventListener('mousedown', cerrar);
  }, [menuAbierto]);

  const abrirCambio = () => {
    setMenuAbierto(false);
    setActual('');
    setNueva('');
    setConfirmacion('');
    setModalAbierto(true);
  };

  const cerrarModal = () => setModalAbierto(false);

  const guardarContrasena = async (evento) => {
    evento.preventDefault();
    if (nueva !== confirmacion) {
      setToastError('Las contraseñas no coinciden');
      return;
    }
    if (!REGLA_CONTRASENA_NUEVA.test(nueva)) {
      setToastError(AVISO_CONTRASENA_DEBIL);
      return;
    }
    try {
      const respuesta = await apiRequest(getApiUrl('/auth/password'), {
        method: 'PUT',
        body: JSON.stringify({
          currentPassword: actual,
          newPassword: nueva,
        }),
      });
      const cuerpo = await respuesta.json().catch(() => ({}));
      if (!respuesta.ok) {
        setToastError(mensajeDeCambioDeContrasena(cuerpo));
        return;
      }
      setModalAbierto(false);
      setToastExito('Contraseña actualizada');
    } catch {
      setToastError('No se pudo cambiar la contraseña');
    }
  };

  return (
    <div className="account-menu-root" ref={raizRef}>
      <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
      <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />
      <button
        type="button"
        className={`account-btn ${menuAbierto ? 'active' : ''}`}
        aria-haspopup="true"
        aria-expanded={menuAbierto}
        onClick={() => setMenuAbierto(abierto => !abierto)}
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="account-btn-icon" aria-hidden="true">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="account-btn-label">Cuenta</span>
      </button>
      {menuAbierto && (
        <div className="account-menu" role="menu">
          <button type="button" className="account-menu-item" role="menuitem" onClick={abrirCambio}>
            Cambiar contraseña
          </button>
          <button
            type="button"
            className="account-menu-item danger"
            role="menuitem"
            onClick={() => {
              setMenuAbierto(false);
              onLogout();
            }}
          >
            Cerrar sesión
          </button>
        </div>
      )}
      {modalAbierto && createPortal(
        <div className="modal-overlay" onClick={cerrarModal}>
          <div
            className="modal-content admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cambiar-password-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="cambiar-password-title">Cambiar contraseña</h3>
              <button type="button" className="modal-close" onClick={cerrarModal}>×</button>
            </div>
            <form onSubmit={guardarContrasena}>
              <div className="form-group">
                <label htmlFor="cuenta-password-actual">Contraseña actual</label>
                <input
                  id="cuenta-password-actual"
                  type="password"
                  value={actual}
                  onChange={e => setActual(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="cuenta-password-nueva">Contraseña nueva</label>
                <input
                  id="cuenta-password-nueva"
                  type="password"
                  value={nueva}
                  onChange={e => setNueva(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="cuenta-password-confirmacion">Confirmar contraseña</label>
                <input
                  id="cuenta-password-confirmacion"
                  type="password"
                  value={confirmacion}
                  onChange={e => setConfirmacion(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="admin-btn secondary" onClick={cerrarModal}>
                  Cancelar
                </button>
                <button type="submit" className="admin-btn primary">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default AccountMenu;
